import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ContainerMediaType } from "@/src/store/apis/tasks/taskDetail/interface";
import {
  DialogParams,
  DialogTab,
  ContainerListItem,
  ContainerMediaData,
  MediaByPhotoType,
  SelectedImageData,
  DialogConfig,
} from "../types";
import { getDialogConfig, getAvailablePhotoTypes } from "../config";
import { useAdapterFactory } from "../adapters";
import { useContainerSelection } from "./useContainerSelection";
import { usePhotoTypeSelection } from "./usePhotoTypeSelection";

/**
 * useContainerPhotosDialog 반환 타입
 */
export interface UseContainerPhotosDialogReturn {
  // Config
  config: DialogConfig;

  // Tab state
  activeTab: DialogTab;
  setActiveTab: (tab: DialogTab) => void;

  // Container data
  containerList: ContainerListItem[] | undefined;
  containerMedia: ContainerMediaData | undefined;
  mediaByPhotoType: MediaByPhotoType | undefined;

  // Selected single container (for detail view)
  selectedSingleContainer: number | undefined;
  setSelectedSingleContainer: (id: number | undefined) => void;

  selectedSinglePhotoType: ContainerMediaType | undefined;
  setSelectedSinglePhotoType: (type: ContainerMediaType | undefined) => void;

  // Container selection (for download)
  containerSelection: {
    selectedContainers: number[];
    addContainer: (id: number) => void;
    removeContainer: (id: number) => void;
    addAllContainers: () => void;
    removeAllContainers: () => void;
    toggleContainer: (id: number) => void;
    isAllSelected: boolean;
  };

  // Photo type selection (for download)
  photoTypeSelection: {
    selectedPhotoTypes: ContainerMediaType[];
    addPhotoType: (type: ContainerMediaType) => void;
    removePhotoType: (type: ContainerMediaType) => void;
    addAllPhotoTypes: () => void;
    removeAllPhotoTypes: () => void;
    togglePhotoType: (type: ContainerMediaType) => void;
    isAllSelected: boolean;
  };

  // Image preview
  selectedImage: SelectedImageData | undefined;
  setSelectedImage: (image: SelectedImageData | undefined) => void;

  // Loading states
  isContainerListLoading: boolean;
  isContainerMediaLoading: boolean;
  isMediaByPhotoTypeLoading: boolean;
  isContainerDownloading: boolean;
  isPhotoTypeDownloading: boolean;

  // Actions
  fetchContainerList: () => Promise<ContainerListItem[] | undefined>;
  fetchContainerMedia: (containerId: number) => Promise<void>;
  fetchMediaByPhotoType: () => Promise<void>;
  downloadSelectedContainers: () => Promise<void>;
  downloadSelectedPhotoTypes: () => Promise<void>;

  // Utilities
  getPhotoTypeLabel: (type: ContainerMediaType) => string;
  availablePhotoTypes: string[];
}

/**
 * ContainerPhotosDialog 메인 훅
 *
 * 다이얼로그의 모든 상태와 기능을 통합 관리합니다.
 * - Adapter를 통한 API 추상화
 * - 탭 상태 관리
 * - 선택 상태 관리 (컨테이너, Photo Type)
 * - 이미지 미리보기 상태
 * - 데이터 페칭 및 다운로드
 *
 * @param params - 다이얼로그 파라미터 (모드별 설정)
 * @param initialSelectedContainer - 초기 선택 컨테이너 ID (선택사항)
 * @returns UseContainerPhotosDialogReturn
 *
 * @example
 * const dialog = useContainerPhotosDialog(
 *   { mode: 'exporter-shipment', shipmentId: 123 },
 *   456 // 초기 선택 컨테이너
 * );
 *
 * // 탭 전환
 * dialog.setActiveTab('photoType');
 *
 * // 컨테이너 선택
 * dialog.containerSelection.toggleContainer(1);
 *
 * // 다운로드
 * await dialog.downloadSelectedContainers();
 */
export const useContainerPhotosDialog = (
  params: DialogParams,
  initialSelectedContainer?: number
): UseContainerPhotosDialogReturn => {
  // Config based on mode
  const config = useMemo(() => getDialogConfig(params.mode), [params.mode]);

  // Tab state
  const [activeTab, setActiveTab] = useState<DialogTab>("container");

  // Selected single container for detail view
  const [selectedSingleContainer, setSelectedSingleContainer] = useState<
    number | undefined
  >(initialSelectedContainer);

  const [selectedImage, setSelectedImage] = useState<
    SelectedImageData | undefined
  >();

  const [selectedSinglePhotoType, setSelectedSinglePhotoType] = useState<
    ContainerMediaType | undefined
  >();

  // Create adapter based on mode (using hook-based factory)
  const adapter = useAdapterFactory(params);

  // Track initialization to prevent duplicate fetches
  const isInitializedRef = useRef(false);
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  // Extract container IDs for selection hook
  const containerIds = useMemo(
    () => adapter.containerList?.map((c) => c.id) ?? [],
    [adapter.containerList]
  );

  // Available photo types from mediaByPhotoType data
  const availablePhotoTypes = useMemo(
    () => getAvailablePhotoTypes(adapter.mediaByPhotoType),
    [adapter.mediaByPhotoType]
  );

  // Selection hooks
  const containerSelection = useContainerSelection(containerIds);
  const photoTypeSelection = usePhotoTypeSelection(availablePhotoTypes);

  // Fetch container list on mount (with cleanup to prevent memory leaks)
  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    let isMounted = true;

    adapterRef.current.fetchContainerList().then((list) => {
      if (!isMounted) return;

      if (list && list.length > 0) {
        const initialId = initialSelectedContainer ?? list[0].id;
        setSelectedSingleContainer(initialId);
        adapterRef.current.fetchContainerMedia(initialId);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [initialSelectedContainer]);

  useEffect(() => {
    if (activeTab === "photoType" && !adapter.mediaByPhotoType) {
      adapter.fetchMediaByPhotoType();
    }
  }, [activeTab, adapter]);

  useEffect(() => {
    if (
      activeTab === "photoType" &&
      availablePhotoTypes.length > 0 &&
      !selectedSinglePhotoType
    ) {
      setSelectedSinglePhotoType(availablePhotoTypes[0]);
    }
  }, [activeTab, availablePhotoTypes, selectedSinglePhotoType]);

  // Fetch container media when selected single container changes
  const handleSelectSingleContainer = useCallback(
    (id: number | undefined) => {
      setSelectedSingleContainer(id);
      if (id !== undefined) {
        adapter.fetchContainerMedia(id);
      }
    },
    [adapter]
  );

  // Download handlers
  const downloadSelectedContainers = useCallback(async () => {
    if (containerSelection.selectedContainers.length === 0) {
      return;
    }
    await adapter.downloadByContainerIds(containerSelection.selectedContainers);
  }, [adapter, containerSelection.selectedContainers]);

  const downloadSelectedPhotoTypes = useCallback(async () => {
    if (photoTypeSelection.selectedPhotoTypes.length === 0) {
      return;
    }
    await adapter.downloadByPhotoTypes(photoTypeSelection.selectedPhotoTypes);
  }, [adapter, photoTypeSelection.selectedPhotoTypes]);

  return {
    // Config
    config,

    // Tab state
    activeTab,
    setActiveTab,

    // Container data
    containerList: adapter.containerList,
    containerMedia: adapter.containerMedia,
    mediaByPhotoType: adapter.mediaByPhotoType,

    selectedSingleContainer,
    setSelectedSingleContainer: handleSelectSingleContainer,

    selectedSinglePhotoType,
    setSelectedSinglePhotoType,

    containerSelection,

    // Photo type selection
    photoTypeSelection,

    // Image preview
    selectedImage,
    setSelectedImage,

    // Loading states
    isContainerListLoading: adapter.isContainerListLoading,
    isContainerMediaLoading: adapter.isContainerMediaLoading,
    isMediaByPhotoTypeLoading: adapter.isMediaByPhotoTypeLoading,
    isContainerDownloading: adapter.isContainerDownloading,
    isPhotoTypeDownloading: adapter.isPhotoTypeDownloading,

    // Actions
    fetchContainerList: adapter.fetchContainerList,
    fetchContainerMedia: adapter.fetchContainerMedia,
    fetchMediaByPhotoType: adapter.fetchMediaByPhotoType,
    downloadSelectedContainers,
    downloadSelectedPhotoTypes,

    // Utilities
    getPhotoTypeLabel: adapter.getPhotoTypeLabel,
    availablePhotoTypes,
  };
};
